import { AsyncPipe } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    effect,
    ElementRef,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    signal,
    SimpleChanges,
    viewChild,
    ViewChild
} from "@angular/core";
import { HotTableComponent, HotTableModule } from "@handsontable/angular-wrapper";
import Handsontable from "handsontable";
import { filter, interval, map } from "rxjs";
import { HotDoc, IHotDocSettings } from "./hotdoc/doc";
import { HotCellAngularRendererService } from "./hotdoc/hot-cell-angular-renderer.service";

@Component({
    selector: "rask-hot-doc",
    templateUrl: "./hot-doc.component.html",
    styleUrls: ["./hot-doc.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        HotTableModule,
        AsyncPipe
    ]
})
export class HotDocComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() canEdit: boolean;
    @ViewChild("containingDiv") containingDiv: ElementRef;
    @Input() hotDoc: HotDoc;

    protected settings: IHotDocSettings = {
        renderAllRows: true,
        afterInit: () => {
            this.hotInstance.set(this.hotTableRef().hotInstance);
        }
    };

    protected sumOfSelected$ = interval(200).pipe(
        filter(() => this.hotDoc?.settings?.raskSettings?.calculateSum),
        map(() => this.hotDoc.sumOfSelected)
    );

    protected renderTable = signal(false);

    private readonly hotTableRef = viewChild(HotTableComponent);
    private readonly hotInstance = signal<Handsontable>(null);

    private readonly hotCellAngularRendererService = inject(HotCellAngularRendererService);
    private readonly ngZone = inject(NgZone);

    constructor() {
        effect(() => {
            const instance = this.hotInstance();
            if (instance) {
                this.initHotDoc(instance);
            }
        });
    }

    bind() {
        this.hotDoc.bind();
    }

    initHotDoc(instance: Handsontable) {
        this.hotDoc.settings.raskInfra.hotCellAngularRendererService = this.hotCellAngularRendererService;
        this.hotDoc.settings.raskInfra.ngZone = this.ngZone;
        this.hotDoc.init(instance);
    }

    disableEditing() {
        this.hotDoc.settings.readOnly = true;
        this.hotDoc.settings.comments = false;
        this.hotDoc.settings.disableVisualSelection = true;
        this.hotDoc.settings.columns.forEach(col => {
            col.editor = false;
        });
    }

    ngAfterViewInit() {
        const resizeObserver = new ResizeObserver(() => {
            // Använder requestAnimationFrame för att försöka rendera innehållet i en frame
            // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
            requestAnimationFrame(() => {
                this.hotDoc?.render();
            });
        });

        resizeObserver.observe(this.containingDiv.nativeElement);
        this.renderTable.set(true);
    }
    ngOnChanges(changes: SimpleChanges) {
        if (changes.hotDoc && this.hotDoc) {
            this.initHotDoc(this.hotInstance());
            this.bind();
            this.settings = {
                ...this.settings,
                ...this.hotDoc.settings
            };
        }
    }
    ngOnInit() {
        if (!this.hotDoc?.settings) {
            return;
        }

        if (this.canEdit === false) {
            this.disableEditing();
        }
    }
}
